import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import { toast } from "sonner";
import { X, Upload, Loader2 } from "lucide-react";
import { departmentsApi, uploadsApi } from "../../api";
import type { Department } from "../../types";

const schema = z.object({
  id: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/, "lowercase, digits, dashes"),
  name: z.string().min(1).max(128),
  slug: z.string().min(1).max(64).regex(/^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/),
  description: z.string().max(512).default(""),
  icon: z.string().min(1).max(64),
  color: z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "must be #RGB or #RRGGBB"),
  banner_url: z.string().url().max(1024).nullable().optional(),
  order: z.coerce.number().int().nonnegative(),
  hidden: z.boolean().default(false),
});
type FormValues = z.infer<typeof schema>;

interface Props {
  existing: Department | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function DepartmentForm({ existing, onClose, onSaved }: Props) {
  const isEdit = !!existing;
  const [uploading, setUploading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: existing
      ? {
          id: existing.id,
          name: existing.name,
          slug: existing.slug,
          description: existing.description,
          icon: existing.icon,
          color: existing.color,
          banner_url: existing.banner_url ?? null,
          order: existing.order,
          hidden: existing.hidden,
        }
      : {
          id: "",
          name: "",
          slug: "",
          description: "",
          icon: "layout-grid",
          color: "#c8453a",
          banner_url: null,
          order: 999,
          hidden: false,
        },
  });

  const bannerUrl = watch("banner_url");

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadsApi.uploadFile(file, "banner");
      setValue("banner_url", url, { shouldDirty: true });
      toast.success("Banner uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function onSubmit(values: FormValues) {
    try {
      if (isEdit) {
        const { id, ...patch } = values;
        await departmentsApi.update(id, patch);
        toast.success("Saved");
      } else {
        await departmentsApi.create(values);
        toast.success("Department created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? "Save failed");
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-sm grid place-items-center p-4 animate-fade-in">
      <div className="card !rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-modal animate-scale-in">
        <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="display text-[18px] font-medium tracking-tight">
            {isEdit ? "Edit department" : "New department"}
          </h2>
          <button onClick={onClose} className="btn-icon" aria-label="close">
            <X className="size-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">ID</label>
              <input className="input font-mono" {...register("id")} disabled={isEdit} placeholder="hr" />
              {errors.id && <p className="text-[11.5px] text-accent-600 mt-1">{errors.id.message}</p>}
            </div>
            <div>
              <label className="label">Slug</label>
              <input className="input font-mono" {...register("slug")} placeholder="hr" />
              {errors.slug && <p className="text-[11.5px] text-accent-600 mt-1">{errors.slug.message}</p>}
            </div>
          </div>

          <div>
            <label className="label">Name</label>
            <input className="input" {...register("name")} placeholder="Human Resources" />
            {errors.name && <p className="text-[11.5px] text-accent-600 mt-1">{errors.name.message}</p>}
          </div>

          <div>
            <label className="label">Description</label>
            <textarea className="input" rows={2} {...register("description")} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Icon</label>
              <input className="input font-mono" {...register("icon")} placeholder="users" />
              <p className="text-[10.5px] text-ink-400 mt-1">lucide-react name</p>
            </div>
            <div>
              <label className="label">Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  className="h-10 w-10 rounded-lg border border-ink-200 cursor-pointer"
                  {...register("color")}
                />
                <input className="input font-mono" {...register("color")} />
              </div>
              {errors.color && <p className="text-[11.5px] text-accent-600 mt-1">{errors.color.message}</p>}
            </div>
            <div>
              <label className="label">Order</label>
              <input type="number" className="input" {...register("order")} />
            </div>
          </div>

          <div>
            <label className="label">Banner image</label>
            <div className="flex items-center gap-2">
              <input className="input flex-1" {...register("banner_url")} placeholder="https://… or leave empty" />
              <label className="btn-secondary cursor-pointer">
                {uploading ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
                Upload
                <input type="file" accept="image/*" hidden onChange={onFile} disabled={uploading} />
              </label>
            </div>
            {bannerUrl && (
              <div
                className="mt-2 h-20 rounded-lg bg-cover bg-center border border-ink-200"
                style={{ backgroundImage: `url(${bannerUrl})` }}
              />
            )}
          </div>

          <label className="flex items-center gap-2 text-[13px] cursor-pointer">
            <input type="checkbox" {...register("hidden")} /> Hidden from viewer
          </label>

          <div className="flex justify-end gap-2 pt-3 border-t border-ink-100 mt-4">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? "Saving…" : isEdit ? "Save changes" : "Create department"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
